import { ChangeDetectionStrategy, Component, signal, ViewEncapsulation } from '@angular/core';
import { MetadataEntityFieldData } from '@jms/model';
import { ICellRendererParams } from 'ag-grid-enterprise';
import { WbValueComponent } from '@workbuddy/ui/v2/components/primitives/value';
import { IdentifierLinkResolver } from '../context';
import { BaseCellRenderer } from './base.cell.renderer';

export interface IdentifierCellParams extends ICellRendererParams {
  field: MetadataEntityFieldData;
  identifierLink: IdentifierLinkResolver;
}

@Component({
  selector: 'identifier-cell',
  standalone: true,
  imports: [WbValueComponent],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    identifier-cell { display: flex; align-items: center; height: 100%; width: 100%; min-width: 0; overflow: hidden; }
    identifier-cell a.identifier-link { color: var(--wb-color-link, #2563eb); text-decoration: none; cursor: pointer; display: inline-flex; align-items: center; min-width: 0; max-width: 100%; overflow: hidden; }
    identifier-cell a.identifier-link wb-value { color: inherit; }
    identifier-cell a.identifier-link:hover wb-value { text-decoration: underline; }
  `],
  template: `
    @if ($href(); as href) {
      <a class="identifier-link" [href]="href" target="_blank" rel="noopener noreferrer" (click)="onClick($event)">
        <wb-value [field]="$field()" [data]="$data()" [truncate]="true" />
      </a>
    } @else {
      <wb-value [field]="$field()" [data]="$data()" [truncate]="true" />
    }
  `,
})
export class IdentifierCellRenderer extends BaseCellRenderer<any, IdentifierCellParams> {

  protected readonly $field = signal<MetadataEntityFieldData | null>(null);
  protected readonly $data = signal<any>(null);
  protected readonly $href = signal<string | null>(null);

  private onClickHandler: ((event: MouseEvent) => void) | null = null;

  public displayLogic(params: IdentifierCellParams): void {

    const field = params.field ?? null;
    const row = params.data;
    const value = params.value;

    this.$field.set(field);
    this.$data.set(row);

    const link = field && params.identifierLink ? params.identifierLink(field, value, row) : null;
    if (link) {
      this.$href.set(link.href ?? null);
      this.onClickHandler = link.onClick ?? null;
    } else {
      this.$href.set(null);
      this.onClickHandler = null;
    }
  }

  protected onClick(event: MouseEvent): void {

    if (this.onClickHandler) this.onClickHandler(event);
  }
}
